import { ServiceProvider } from "@sa/rep-services-provider";
import { MapStore } from "../store/MapStore";
import { ServicesMaps } from "../MapReportingProvider";
import { MapDataSource, MapDataSourceId } from "../models/MapDataSource";
import { MapDimension, MapDimensionType } from "../models/MapDimension";
import { combineLatest } from "rxjs";
import { getDefaultYear } from "../utils/getDefaultYear";
import { DictionariesService } from "@sa/rep-mdm";
import { debounceTime } from "rxjs/operators";
import { orderBy } from "lodash";

export class MapDefaultMarkService {
  constructor(private serviceProvider: ServiceProvider) {}

  subscribe() {
    const store = this.serviceProvider.resolve<MapStore>(ServicesMaps.MapStore);
    const dictionariesService = this.serviceProvider.resolve<DictionariesService>(ServicesMaps.Dictionaries);
    return combineLatest([store.dataSource$, store.dimensions$, store.loadingDimensions$, store.favouriteItem$])
      .pipe(debounceTime(300))
      .subscribe(([dataSource, dimensions, loadingDimensions, favouriteItem]) => {
        if (!loadingDimensions) {
          dimensions.forEach(async (dim) => {
            const dimValueStream$ = store.dimensionsValues.get(dim.id)!;
            dimValueStream$.next(null);
            if (dim.type === MapDimensionType.Calendar) {
              const year = await getDefaultYear(dictionariesService, dataSource);
              dimValueStream$.next(year);
            } else {
              const hierarchy = dim.hierarchy.split(".")[0];
              const dictionary = await dictionariesService.getDictionary({ id: dim.identifier, parameters: {} });
              const items = await dictionary.getAll();
              const getName = (e: any) => {
                const nameIsPrimitive = typeof e.getValue("name") !== "object";
                const name = nameIsPrimitive ? e.getValue("name") : e.getValue("name")[9];
                return `${name}`.toLowerCase().trim();
              };
              const findItemIdByName = (name: string) => {
                const searchName = name.toLowerCase().trim();
                const primaryCandidates = orderBy(
                  items.filter((e) => getName(e) === searchName),
                  (e) => e.values["parent"],
                  "asc"
                );
                const secondaryCandidates = orderBy(
                  items.filter((e) => getName(e).includes(searchName)),
                  (e) => e.values["parent"],
                  "asc"
                );
                let item = primaryCandidates[0];
                item = item || secondaryCandidates[0];
                item = item || items[0];
                return item?.getValue<number>("id");
              };

              switch (hierarchy) {
                case "commodities": {
                  switch (dataSource.id) {
                    case MapDataSourceId.HarmonizedDataset:
                    case MapDataSourceId.USDA_PSD: {
                      dimValueStream$.next(findItemIdByName("corn grain"));
                      break;
                    }
                    case MapDataSourceId.UNComtrade:
                    case MapDataSourceId.FAOProdStat:
                    case MapDataSourceId.FAPRI:
                    case MapDataSourceId.FAPRI2012:
                    case MapDataSourceId.OECD_FAO: {
                      dimValueStream$.next(findItemIdByName("cereals"));
                      break;
                    }
                    case MapDataSourceId.USDAForecast: {
                      dimValueStream$.next(findItemIdByName("barley"));
                      break;
                    }
                    default:
                      dimValueStream$.next(items[0]?.getValue("id"));
                      break;
                  }
                  break;
                }
                case "attributes": {
                  dimValueStream$.next(findItemIdByName("area"));
                  break;
                }
                case "flow": {
                  dimValueStream$.next(findItemIdByName("export"));
                  break;
                }
                case "reporting_countries":
                case "countries": {
                  if (dataSource.id === MapDataSourceId.UNComtrade) {
                    dimValueStream$.next(findItemIdByName("France"));
                  } else {
                    dimValueStream$.next(findItemIdByName("Worldwide"));
                  }
                  break;
                }
                case "units": {
                  switch (dataSource.id) {
                    case MapDataSourceId.HarmonizedDataset: {
                      dimValueStream$.next([3, 7, 9, 8]);
                      break;
                    }
                    case MapDataSourceId.UNComtrade: {
                      dimValueStream$.next([8]);
                      break;
                    }
                    case MapDataSourceId.USDA_PSD: {
                      dimValueStream$.next([9, 8, 44, 7]);
                      break;
                    }
                    case MapDataSourceId.OECD_FAO: {
                      dimValueStream$.next([9, 8, 7]);
                      break;
                    }
                    case MapDataSourceId.USDAForecast: {
                      dimValueStream$.next([45, 46, 44, 7]);
                      break;
                    }
                    case MapDataSourceId.FAPRI2012: {
                      dimValueStream$.next([39, 40, 41, 44]);
                      break;
                    }
                    case MapDataSourceId.FAPRI: {
                      dimValueStream$.next([45, 46, 43, 42, 47]);
                      break;
                    }
                    case MapDataSourceId.FAPRI: {
                      dimValueStream$.next([45, 46, 43, 42, 47]);
                      break;
                    }
                    case MapDataSourceId.FAOProdStat: {
                      dimValueStream$.next([39, 51, 50]);
                      break;
                    }
                  }
                  break;
                }
                default:
                  dimValueStream$.next(items[0]?.getValue("id"));
                  break;
              }

              const favouriteItemDataSourceId = Number(favouriteItem?.mark.parameters["@data_source"]?.[0]);
              const favouriteItemDimValue = favouriteItem?.mark.parameters[`@${hierarchy}`]?.[0];

              if (favouriteItem && favouriteItemDataSourceId === dataSource.id && favouriteItemDimValue !== undefined) {
                dimValueStream$.next(Number(favouriteItemDimValue));
              }
            }
          });
        }
      });
  }
}
